import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Clock, CornerDownLeft, Command } from "lucide-react";
import { bouwIndex, zoek, leesRecent, onthoudRecent } from "@/lib/menuZoek";

const inInvoer = (el) => el && (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable);

/* Voorstel 55 — zoekveld 'Zoek functie…' in het linkermenu. donker = hub-balk (desktop), licht = menulade (mobiel). */
export function MenuZoeker({ hubs, donker = true, collapsed = false, onExpand, onNavigate, autoFocus = false }) {
  const nav = useNavigate();
  const ref = useRef(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const [wilFocus, setWilFocus] = useState(autoFocus);
  const index = useMemo(() => bouwIndex(hubs), [hubs]);
  const treffers = useMemo(() => (q.trim() ? zoek(index, q) : leesRecent(index)), [index, q, open]); // eslint-disable-line react-hooks/exhaustive-deps
  const recent = !q.trim();

  useEffect(() => { if (wilFocus && !collapsed && ref.current) { ref.current.focus(); setWilFocus(false); } }, [wilFocus, collapsed]);
  useEffect(() => {
    const h = (e) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey || inInvoer(document.activeElement)) return;
      if (!donker && !ref.current) return; // mobiel: alleen als de lade open is
      e.preventDefault();
      if (collapsed) { onExpand?.(); setWilFocus(true); } else ref.current?.focus();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [collapsed, onExpand, donker]);
  useEffect(() => setSel(0), [q]);

  const kies = (it) => {
    if (!it) return;
    onthoudRecent(it.to);
    nav(it.to);
    setQ(""); setOpen(false); ref.current?.blur(); onNavigate?.();
  };
  const naarAlles = () => { window.dispatchEvent(new CustomEvent("bpt:open-cmdk", { detail: { query: q } })); setQ(""); setOpen(false); onNavigate?.(); };
  const toets = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, treffers.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); treffers[sel] ? kies(treffers[sel]) : q.trim() && naarAlles(); }
    else if (e.key === "Escape") { e.preventDefault(); if (q) setQ(""); else { setOpen(false); ref.current?.blur(); } }
  };

  if (collapsed) {
    return (
      <button type="button" onClick={() => { onExpand?.(); setWilFocus(true); }} title="Zoek functie (/)" aria-label="Zoek functie"
              className="mx-auto mt-1 mb-1 flex h-9 w-9 items-center justify-center rounded-md text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="menu-zoek-knop">
        <Search className="h-4 w-4" />
      </button>
    );
  }
  const veld = donker ? "bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500 focus:bg-slate-800 focus:border-slate-500" : "bg-slate-50 border-slate-200 text-slate-800 focus:bg-white focus:border-[hsl(var(--brand-blue))]";
  return (
    <div className={`relative ${donker ? "px-2 pt-2 pb-1" : "px-1 pb-2"}`} data-testid="menu-zoeker" onClick={(e) => e.stopPropagation()}>
      <div className="relative">
        <Search className={`pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 ${donker ? "text-slate-500" : "text-slate-400"}`} />
        <input ref={ref} type="text" value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
               onKeyDown={toets} placeholder="Zoek functie…  (/)" aria-label="Zoek functie" autoComplete="off" spellCheck={false}
               className={`w-full rounded-md border py-2 pl-8 pr-7 text-[13px] outline-none transition-colors ${veld}`} data-testid="menu-zoek-veld" />
        {q && <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setQ(""); ref.current?.focus(); }} aria-label="Wis" className={`absolute right-2 top-2 ${donker ? "text-slate-500 hover:text-white" : "text-slate-400 hover:text-slate-700"}`} data-testid="menu-zoek-wis"><X className="h-3.5 w-3.5" /></button>}
      </div>
      {open && (treffers.length > 0 || q.trim()) && (
        <div className={`absolute left-2 right-2 z-50 mt-1 overflow-hidden rounded-md border shadow-xl ${donker ? "w-[320px] border-slate-700 bg-slate-900" : "border-slate-200 bg-white"}`} role="listbox" data-testid="menu-zoek-resultaten">
          {recent && treffers.length > 0 && <div className={`px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] ${donker ? "text-slate-500" : "text-slate-400"}`}>Laatst gebruikt</div>}
          {treffers.map((it, i) => {
            const Icon = it.icon || Search;
            const actief = i === sel;
            return (
              <button key={it.key} type="button" role="option" aria-selected={actief} onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setSel(i)} onClick={() => kies(it)}
                      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left ${actief ? (donker ? "bg-slate-700 text-white" : "bg-blue-50 text-slate-900") : (donker ? "text-slate-200 hover:bg-slate-800" : "text-slate-800 hover:bg-slate-50")}`}
                      data-testid={`menu-zoek-treffer-${it.tid}`}>
                {recent ? <Clock className="h-4 w-4 shrink-0 text-slate-400" /> : <Icon className={`h-4 w-4 shrink-0 ${donker ? "text-slate-400" : "text-slate-500"}`} />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium">{it.label}</span>
                  <span className={`block truncate text-[10px] uppercase tracking-wide ${donker ? "text-slate-500" : "text-slate-400"}`}>{it.pad}</span>
                </span>
                {actief && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-slate-500" />}
              </button>
            );
          })}
          {!recent && treffers.length === 0 && <div className={`px-3 py-2 text-xs italic ${donker ? "text-slate-500" : "text-slate-400"}`} data-testid="menu-zoek-leeg">Geen functie gevonden.</div>}
          {q.trim() && (
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={naarAlles}
                    className={`flex w-full items-center gap-2 border-t px-3 py-2 text-left text-xs ${donker ? "border-slate-800 text-sky-300 hover:bg-slate-800" : "border-slate-100 text-blue-700 hover:bg-blue-50"}`} data-testid="menu-zoek-alles">
              <Command className="h-3.5 w-3.5 shrink-0" /><span className="min-w-0 truncate">Zoek "<b>{q}</b>" in alle data (⌘K)</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
